import { PageLoader } from '@/components/ui/loader';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, KeyRound, PhoneCall, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Switch } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

type Provider = '3cx' | 'yeastar' | 'generic';

type Integration = {
  id: string;
  provider: Provider;
  name: string;
  isActive: boolean;
  extensions: Record<string, string>;
  createLeadOnMissed: boolean;
  popupIncoming: boolean;
  lastEventAt: string | null;
  lastError: string | null;
  eventCount: number;
  token: string;
  eventsUrl: string;
  lookupUrl: string;
};

const PROVIDER_LABELS: Record<Provider, string> = { '3cx': '3CX', yeastar: 'Yeastar', generic: 'Other PBX (webhook)' };

const STEPS: Record<Provider, string[]> = {
  '3cx': [
    '3CX Admin → Settings → CRM Integration → choose “Generic (JSON/REST)” or upload a custom template.',
    'Contact lookup URL: paste the lookup URL below and add [Number] at the end.',
    'Call journaling: turn it on and point it at the events URL below (method POST). Send CallType, Number, Agent, Duration and the recording link.',
    'Map each 3CX extension number to its DovoCRM user below.',
  ],
  yeastar: [
    'Yeastar Management Portal → Integrations → API → turn on the API and add a Webhook.',
    'Webhook URL: paste the events URL below. Tick “Call End Details” (30012) and “Call State Changed” (30011).',
    'Optional: Integrations → Contacts lookup → HTTP, with the lookup URL below.',
    'Map each Yeastar extension number to its DovoCRM user below.',
  ],
  generic: [
    'Have the PBX POST JSON to the events URL for each call: event (ringing / ended), callId, direction, from, to, extension, duration, status, recordingUrl.',
    'For caller ID, call the lookup URL with the number at the end — it answers with the matching contact or lead.',
    'Map each extension number to its DovoCRM user below.',
  ],
};

/**
 * The phone system (AmoCRM's and HubSpot's telephony integrations): a 3CX or
 * Yeastar PBX — the systems UAE offices actually run — connected by webhook.
 */
export function TelephonyPage() {
  const queryClient = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({ queryKey: ['sales', 'telephony'], queryFn: () => apiGet<Integration[]>('/sales/telephony') });
  const [editing, setEditing] = useState<Integration | 'new' | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['sales', 'telephony'] });
  const rotate = useMutation({
    mutationFn: (id: string) => apiPost(`/sales/telephony/${id}/rotate`),
    onSuccess: () => {
      refresh();
      toast.success('New token made — update the URLs in your PBX');
    },
  });

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value).catch(() => undefined);
    toast.success('Copied');
  };

  return (
    <div>
      <PageHeader
        title="Phone system"
        description="Connect your office PBX. Incoming calls pop up for the right person with the customer’s record, every call and its recording is logged automatically, and missed calls from new numbers become incoming leads."
        breadcrumbs={[{ label: 'Sales hub' }, { label: 'Phone system' }]}
        actions={
          <Can permission="crm.telephony.update">
            <Button onClick={() => setEditing('new')}>
              <Plus /> Connect a PBX
            </Button>
          </Can>
        }
      />

      {isLoading ? (
        <PageLoader />
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center">
          <PhoneCall className="mx-auto h-10 w-10 text-primary/60" />
          <p className="mt-3 font-semibold">No phone system connected</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Works with 3CX, Yeastar, and any PBX that can call a webhook. Without one, reps can still click Call on a record
            and log it by hand.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {rows.map((row) => (
            <Card key={row.id}>
              <CardHeader className="flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="text-base">{row.name}</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {PROVIDER_LABELS[row.provider]} · {row.eventCount} events
                    {row.lastEventAt ? ` · last ${formatDateTime(row.lastEventAt)}` : ' · waiting for the first call'}
                  </p>
                  {row.lastError && <p className="mt-1 text-xs text-destructive">{row.lastError}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={row.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}>{row.isActive ? 'On' : 'Off'}</Badge>
                  <Can permission="crm.telephony.update">
                    <Button size="sm" variant="outline" onClick={() => setEditing(row)}>
                      Settings
                    </Button>
                  </Can>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {(
                  [
                    ['Events URL (call reports)', row.eventsUrl],
                    ['Lookup URL (caller ID) — the number goes at the end', row.lookupUrl],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label} className="space-y-1">
                    <Label className="text-xs">{label}</Label>
                    <div className="flex gap-2">
                      <code className="min-w-0 flex-1 truncate rounded-lg bg-muted/60 px-3 py-2 text-xs">{value}</code>
                      <Button size="sm" variant="outline" onClick={() => copy(value)}>
                        <Copy />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="rounded-xl bg-primary/[0.04] p-4">
                  <p className="text-sm font-semibold">Set up in {PROVIDER_LABELS[row.provider]}</p>
                  <ol className="mt-2 list-decimal space-y-1 ps-5 text-sm text-muted-foreground">
                    {STEPS[row.provider].map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">{Object.keys(row.extensions).length} extensions mapped</span>
                  <Can permission="crm.telephony.update">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.confirm('Make a new token? The PBX stops reaching DovoCRM until you paste the new URLs.') && rotate.mutate(row.id)}
                    >
                      <KeyRound /> New token
                    </Button>
                  </Can>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && <TelephonyEditor row={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </div>
  );
}

function TelephonyEditor({ row, onClose, onSaved }: { row: Integration | null; onClose: () => void; onSaved: () => void }) {
  const { data: users = [] } = useUserOptions();
  const [provider, setProvider] = useState<Provider>(row?.provider ?? '3cx');
  const [name, setName] = useState(row?.name ?? 'Office PBX');
  const [extensions, setExtensions] = useState<[string, string][]>(
    row && Object.keys(row.extensions).length ? Object.entries(row.extensions) : [['101', '']],
  );
  const [createLeadOnMissed, setCreateLeadOnMissed] = useState(row?.createLeadOnMissed ?? true);
  const [popupIncoming, setPopupIncoming] = useState(row?.popupIncoming ?? true);
  const [isActive, setIsActive] = useState(row?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        provider,
        name,
        extensions: Object.fromEntries(extensions.filter(([extension, userId]) => extension.trim() && userId)),
        createLeadOnMissed,
        popupIncoming,
        isActive,
      };
      return row ? apiPatch(`/sales/telephony/${row.id}`, body) : apiPost('/sales/telephony', body);
    },
    onSuccess: () => {
      onSaved();
      toast.success('Saved');
      onClose();
    },
    onError: (failure) => setError(failure instanceof ApiRequestError ? failure.message : 'Could not save'),
  });
  const remove = useMutation({
    mutationFn: () => apiDelete(`/sales/telephony/${row?.id}`),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{row ? 'Phone system settings' : 'Connect a PBX'}</DialogTitle>
          <DialogDescription>After saving, paste the two URLs into your PBX.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>PBX</Label>
              <Select value={provider} onChange={(event) => setProvider(event.target.value as Provider)}>
                {Object.entries(PROVIDER_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Extensions</Label>
            {extensions.map(([extension, userId], index) => (
              <div key={index} className="flex gap-2">
                <Input
                  className="w-28"
                  placeholder="101"
                  value={extension}
                  onChange={(event) => setExtensions((current) => current.map((item, at) => (at === index ? [event.target.value.replace(/\D/g, ''), item[1]] : item)))}
                />
                <Select value={userId} onChange={(event) => setExtensions((current) => current.map((item, at) => (at === index ? [item[0], event.target.value] : item)))}>
                  <option value="">Choose a person…</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </Select>
                <Button size="sm" variant="ghost" onClick={() => setExtensions((current) => current.filter((_, at) => at !== index))}>
                  <Trash2 />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setExtensions((current) => [...current, ['', '']])}>
              <Plus /> Add extension
            </Button>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={popupIncoming} onCheckedChange={setPopupIncoming} /> Pop incoming calls up for the person ringing
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={createLeadOnMissed} onCheckedChange={setCreateLeadOnMissed} /> A missed call from a new number becomes an incoming lead
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={isActive} onCheckedChange={setIsActive} /> Connected
          </label>
          <FormError message={error} />
        </div>
        <DialogFooter className={row ? 'sm:justify-between' : undefined}>
          {row && (
            <Button variant="ghost" className="text-destructive" onClick={() => window.confirm('Disconnect this PBX?') && remove.mutate()}>
              <Trash2 /> Remove
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || name.trim().length < 2}>
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
